import styles from './Post.module.scss';
import {gql} from "@apollo/client";
import {useInView} from "framer-motion";
import {useShareDataStore} from "@/lib/stores/post";
import className from "classnames/bind";
import PropTypes from "prop-types";
import {memo, Suspense, useContext, useEffect, useMemo, useRef, useState} from "react";
import dynamic from 'next/dynamic';
import { twMerge } from 'tailwind-merge'
import {getPrimaryCategory} from "@/lib/utils/helpers";
import {sendIpsosEvent} from "@/lib/utils/ipsos";
import PostHeader from "@/components/PostHeader/PostHeader";
import Heading from "@/components/Heading/Heading";
import PostCard from "@/components/PostCard/PostCard";
import PostFooter from "@/components/PostFooter/PostFooter";
import Container from "@/components/Container/Container";
import ContentWrapper from "@/components/ContentWrapper/ContentWrapper";
import {SlotLevelAdTargetContext} from "@/lib/utils/context";
import InjectBanner from "@/components/InjectBanner/InjectBanner";
import Loading from "@/components/Loading/Loading";
import Newsletter from "@/components/Newsletter/Newsletter";
import classNames from "classnames";
import PostInfo from "@/components/PostInfo/PostInfo";
import TermLinks from "@/components/TermLinks/TermLinks";

const cx = className.bind(styles);
const PostsGrid = dynamic(() => import('@/components/PostsGrid/PostsGrid').then((mod) => mod.default));
const ShareBar = dynamic(() => import('@/components/ShareBar/ShareBar').then((mod) => mod.default), {ssr: false});
const RelatedStories = dynamic(() => import('@/components/RelatedStories/RelatedStories').then((mod) => mod.default));
const PostSidebar = dynamic(() => import('@/components/PostSidebar/PostSidebar').then((mod) => mod.default));
const AdSlotPostSideBar = dynamic(() => import('@/components/GPT/GPT').then((mod) => mod.AdSlot.PostSideBar));
const AdSlotFooterSticky = dynamic(() => import('@/components/GPT/GPT').then((mod) => mod.AdSlot.FooterSticky));

// Track postInView in child component to avoid Post re-rendering
const PostWrapper = function ({post, children, ...props}) {
  const slotLevelAdTargetContext = useContext(SlotLevelAdTargetContext);
  const setShareData = useShareDataStore((state) => state.setData);

  const {momPostLayerData} = post;
  const {section} = slotLevelAdTargetContext || {};

  // Parse once, reuse across renders
  const parsedPostLayerData = useMemo(() => {
    if (!momPostLayerData) return null;
    try {
      return JSON.parse(momPostLayerData);
    } catch (e) {
      console.error('Failed to parse momPostLayerData:', e);
      return null;
    }
  }, [momPostLayerData]);

  // Track if post is in view with SSR safety
  const ref = useRef(null);
  const [isMounted, setIsMounted] = useState(false);
  const hasTrackedAnalytics = useRef(false);
  
  // Ensure component is mounted before using IntersectionObserver
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsMounted(true);
    }
  }, []);

  // Only use useInView after component is mounted to prevent ref attachment issues
  const isPostInView = useInView(
    isMounted ? ref : { current: null },
    {
      margin: `-50% 0px -50% 0px`,
      once: false,
    }
  );

  const urlChangeTimer = useRef(null);

  useEffect(() => {
    if (isPostInView && isMounted && typeof window !== 'undefined') {
      const title = post?.seo?.title || post.title;

      // Debounce to avoid rapid toggling from ad layout shifts
      if (window.location.pathname !== post.uri && !window.location.pathname.includes('/draft/')) {
        clearTimeout(urlChangeTimer.current);
        urlChangeTimer.current = setTimeout(() => {
          // Re-check after debounce in case another post took over
          if (window.location.pathname === post.uri) return;

          history.replaceState(null, title, post.uri);

          // IPSOS
          sendIpsosEvent(section);
        }, 300);
      }

      // GA4: Push article_view_datalayer once per post instance, regardless of URL match
      if (!hasTrackedAnalytics.current && parsedPostLayerData) {
        hasTrackedAnalytics.current = true;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(parsedPostLayerData);
      }

      // Update share data
      setShareData({
        url: post.uri,
        title: post.title,
        imageUrl: post.featuredImage?.node?.sourceUrl
      });
    }

    return () => clearTimeout(urlChangeTimer.current);
  }, [isPostInView, isMounted]);

  return <div ref={ref} {...props}>{children}</div>;
};

export default function Post(
  {
    post,
    latestNews,
    promotedPosts,
    siteSettings,
    className,
    positionIndex = 0,
  }
) {
  const {
    content,
    categories,
    momRelatedStories,
    momAdTargetingPost,
    momShowAd
  } = post;
  // Leave here in case we go back to keyLatestPosts
  // latestNews = latestNews?.edges?.map((edge) => edge.node)?.filter((node) => node.databaseId !== post.databaseId);

  // Exclude the post itself from the lists (failsafe)
  promotedPosts = useMemo(() => promotedPosts?.filter((p) => p.databaseId !== post.databaseId), [promotedPosts, post.databaseId]);
  latestNews = useMemo(() => latestNews?.filter((p) => p.databaseId !== post.databaseId), [latestNews, post.databaseId]);

  // Ad targeting
  const slotLevelTargeting = useMemo(() => {
    try {
      return JSON.parse(momAdTargetingPost) || {};
    } catch (e) {
      console.error('Failed to parse momAdTargetingPost:', e);
      return {};
    }
  }, [momAdTargetingPost]);

  // Custom fields
  const {
    isFeaturedPost,
    normalPostBlock: {showingBig: normalFullWidth,} = {},
    featuredPostBlock: {fullOrStandard: featuredFullWidth,} = {}
  } = post?.postSettings || {};

  // Primary category
  const primaryCategory = getPrimaryCategory(categories?.edges);

  const isFullWidth =
    isFeaturedPost ?
      ('full' === featuredFullWidth?.toLowerCase()) :
      ('big' === normalFullWidth?.toLowerCase());

  // Determine if featured image should be preloaded
  const preloadFeaturedImage = positionIndex === 0;

  return (
    <SlotLevelAdTargetContext.Provider value={slotLevelTargeting}>
      <PostWrapper
        post={post}
        className={cx(
          'component',
          {'size--large': isFullWidth || isFeaturedPost},
          {'variant--featured': isFeaturedPost},
          className
        )}
      >
        <Suspense fallback={<Loading/>}>
          {siteSettings && (
            <InjectBanner
              hasContainer={true}
              settings={siteSettings}
              positionDesktop={2}
              positionMobile={1}
              className={`mt-4`}
            />
          )}

          {(isFeaturedPost || isFullWidth) && (
            <Container className={`mom-intrinsic pb-4`}>
              <PostHeader post={post} preloadImage={preloadFeaturedImage}/>

              {(isFeaturedPost) && (<PostInfo align={'center'} hideTOC={true} className={`mb-6`} {...{post}}/>)}
            </Container>
          )}

          {isFeaturedPost ? (
            <>
              {isFullWidth ? (
                <>
                  <ContentWrapper
                    content={content}
                    isFullWidth={true}
                    showAd={momShowAd}
                    postPositionIndex={positionIndex}
                    className={twMerge(cx(['mom-featured']))}
                  />
                </>
              ) : (
                <Container className={cx('container')}>
                  <ContentWrapper
                    content={content}
                    isFullWidth={false}
                    showAd={momShowAd}
                    postPositionIndex={positionIndex}
                    className={twMerge(cx(['mom-featured']))}
                  />
                </Container>
              )}
              <Container className={cx('container mom-intrinsic')}>
                <PostFooter post={post}/>
              </Container>
            </>
          ) : (
            <Container className={cx('container')}>
              <div className={cx('contentWrapper')}>
                <ContentWrapper
                  content={content}
                  showPaywall={true}
                  isFullWidth={isFullWidth}
                  showAd={momShowAd}
                  postPositionIndex={positionIndex}
                  headerContent={!isFullWidth ? <PostHeader post={post} preloadImage={preloadFeaturedImage}/> : null}
                  beforeContent={
                    /* This is the best way so far to add post info block to above content, while we still can support featured post */
                    <PostInfo className={`mb-6`} {...{post}}/>
                  }
                  railContent={
                    <ShareBar
                      url={post.uri}
                      shareTitle={post?.title}
                      shareVia={'manofmany'}
                      className={classNames(`sticky top-offset-t-mini-alt ml-auto mr-[18px]`)}
                      facebook x email copy save
                      storyId={post.databaseId}
                    />
                  }
                />

                <PostFooter post={post}/>
              </div>

              <PostSidebar className={classNames(
                'mom-intrinsic',
                'hidden xl:block',
                'xl:flex-none xl:w-[var(--mom--sidebar)]',
                {'xl:pt-[45px]': !isFullWidth}
              )}>
                {momShowAd && (
                  <div className={`mb-5`}>
                    <AdSlotPostSideBar postPositionIndex={positionIndex}/>
                  </div>
                )}

                <Heading className={classNames(`mom-subheading`, {'pt-5 mom-border border-t-2': momShowAd})} level={'h3'}>Trending Stories</Heading>
                <PostsGrid posts={latestNews.slice(0, 5)} columnsPerRow={1} variant={'small'} gridClassName={`grid-cols-1`}/>

                <Newsletter className={`text-center mom-hidden-auth`} variant={'sidebar'} description={''} showAwards={true}/>

                <PostsGrid posts={promotedPosts} columnsPerRow={1} isFeatured={true} gridClassName={`grid-cols-1`}/>

                {siteSettings && (
                  <InjectBanner
                    settings={siteSettings}
                    positionDesktop={1}
                    positionMobile={0}
                    className={`mb-4`}
                    preloadImage={false}
                  />
                )}

                {momShowAd && (
                  <div className={`sticky top-[calc(var(--mom--offset-top-mini)_+_15px)]`}>
                    <AdSlotPostSideBar postPositionIndex={positionIndex} adIndex={1}/>
                  </div>
                )}
              </PostSidebar>
            </Container>
          )}

          {momShowAd && (
            <AdSlotFooterSticky postPositionIndex={positionIndex} objectType={'article'}/>
          )}

          <Container className={`mom-section py-6 xl:hidden mom-intrinsic`} edgePadding={false}>
            <Heading className={`mom-subheading mx-4 xl:mx-0`} level={'h3'}>Trending Stories</Heading>
            <PostsGrid
              posts={latestNews}
              className={`pl-4 xl:pl-container xl:-mx-container`}
              columnsPerRow={1}
              variant={'small'}
              gridClassName={`grid-cols-1`}
              scrollable={true}
            />
          </Container>

          {/* No need to check postSettings.postDisableRelatedPosts, it has been checked from backend */}
          {(momRelatedStories && momRelatedStories?.length > 0) && (
            <Container className={`mom-section mom-intrinsic`}>
              <RelatedStories posts={momRelatedStories} seeMoreUrl={primaryCategory?.node?.uri}/>
            </Container>
          )}
        </Suspense>
      </PostWrapper>
    </SlotLevelAdTargetContext.Provider>
  );
}

export const PostMemo = memo((props) => {
  return (
    <Post {...props}/>
  );
});

Post.fragments = {
  keyPost: 'PostFragment',
  entryPost: gql`
    ${PostHeader.fragments.entry}
    ${PostFooter.fragments.entry}
    ${PostCard.fragments.entry}
    ${TermLinks.fragments.entryCategory}
    fragment PostFragment on Post {
      ...${PostHeader.fragments.key}
      ...${PostFooter.fragments.key}
      databaseId
      content
      uri
      postSettings {
        isFeaturedPost
        featuredPostBlock {
          fullOrStandard
        }
        normalPostBlock {
          showingBig
        }
      }
      momShowAd
      momAdTargetingPost
      momPostLayerData
      categories {
        ...${TermLinks.fragments.keyCategory}
      }
      momRelatedStories {
        ...${PostCard.fragments.key}
      }
    }
  `,

  /*keyLatestPosts: 'LatestPostsFragment',
  entryLatestPosts: gql`
    ${PostCard.fragments.entry}
    fragment LatestPostsFragment on RootQueryToPostConnection {
      edges {
        node {
          ...${PostCard.fragments.key}
        }
      }
    }
  `*/
};

Post.propTypes = {
  post: PropTypes.object.isRequired,
  /*latestNews: PropTypes.object,*/ // Leave here in case we go back to keyLatestPosts
  latestNews: PropTypes.arrayOf(PropTypes.object),
  promotedPosts: PropTypes.arrayOf(PropTypes.object),
  className: PropTypes.string,
  positionIndex: PropTypes.number,
};
